import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { isSuperAdmin, isGlobalAdmin, canCreateProject, isGlobalViewer, isProjectMember, isViewerOnly, canManageUsers } from "../utils/permissions";
import UserMenu from "../components/UserMenu";

export default function SelectProject() {
  const [projects, setProjects] = useState<any[]>([]);
  const [selected, setSelected] = useState("");
  const [search, setSearch] = useState("");
  const [showAccessPopup, setShowAccessPopup] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState<{ project_id: string; project_name: string; requested_role: string; note: string } | null>(null);
  const [myRequests, setMyRequests] = useState<any[]>([]);
  const [submitMsg, setSubmitMsg] = useState("");
  const popupShown = useRef(false);
  const navigate = useNavigate();
  const name = localStorage.getItem("name") || "User";

  useEffect(() => {
    api.get("/api/projects").then(r => {
      setProjects(r.data);
      const accessible = r.data.filter((p: any) => !p.viewer_only);
      if (accessible.length > 0) setSelected(accessible[0].id);
      // Show access request popup for viewer-only users after 2.5s
      if (isViewerOnly() && !popupShown.current) {
        popupShown.current = true;
        setTimeout(() => setShowAccessPopup(true), 2500);
      }
    }).catch(() => navigate("/login"));
    if (isGlobalViewer()) {
      api.get("/api/access-requests/my").then(r => setMyRequests(r.data)).catch(() => {});
    }
  }, []);

  const filtered = projects.filter(p =>
    search === "" ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (status: string) => {
    if (status === "active") return "text-green-600 bg-green-50 border border-green-200";
    if (status === "inactive") return "text-gray-500 bg-gray-100 border border-gray-200";
    return "text-orange-500 bg-orange-50 border border-orange-200";
  };

  const cpiColor = (cpi: number) => {
    if (!cpi) return "text-gray-400";
    if (cpi < 0.845) return "text-red-500";
    if (cpi < 0.945) return "text-amber-500";
    return "text-green-600";
  };

  const cpiDot = (cpi: number) => {
    if (!cpi) return "bg-gray-300";
    if (cpi < 0.845) return "bg-red-500";
    if (cpi < 0.945) return "bg-amber-400";
    return "bg-green-500";
  };

  const handleEnter = () => {
    if (!selected) return;
    localStorage.setItem("active_project_id", selected);
    const proj = projects.find(p => p.id === selected);
    if (proj) localStorage.setItem("active_project", JSON.stringify(proj));
    navigate(`/dashboard/${selected}`);
  };

  const openRequestModal = (p: any) => {
    setRequestForm({ project_id: p.id, project_name: p.name, requested_role: "viewer", note: "" });
    setShowRequestModal(true);
    setShowAccessPopup(false);
    setSubmitMsg("");
  };

  const submitRequest = async () => {
    if (!requestForm) return;
    try {
      await api.post("/api/access-requests", {
        project_id: requestForm.project_id,
        requested_role: requestForm.requested_role,
        note: requestForm.note || undefined,
      });
      setSubmitMsg("Request submitted! You will be notified once approved.");
      api.get("/api/access-requests/my").then(r => setMyRequests(r.data)).catch(() => {});
      setTimeout(() => { setShowRequestModal(false); setSubmitMsg(""); }, 2000);
    } catch (e: any) {
      setSubmitMsg(e.response?.data?.detail || "Failed to submit request");
    }
  };

  const hasPendingRequest = (projectId: string) =>
    myRequests.some(r => r.project_id === projectId && r.status === "pending");

  const hasApprovedRequest = (projectId: string) =>
    myRequests.some(r => r.project_id === projectId && r.status === "approved");

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <div className="text-xs text-gray-400 leading-none">COLLINS</div>
            <div className="text-sm font-semibold text-gray-900 leading-tight">EAC Reporting</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!isViewerOnly() && !isGlobalAdmin() && (
            <button onClick={handleEnter} className="px-4 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium">Dashboard</button>
          )}
          <button className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Report</button>
          <button className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">History</button>
          {canManageUsers() && (
            <button onClick={() => navigate("/users")} className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">User Management</button>
          )}
        </div>
        <UserMenu />
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SELECT A PROJECT</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              {isViewerOnly()
                ? "You can see all projects. Request access to join a project."
                : "Choose which program this quarterly EAC report belongs to."}
            </p>
          </div>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search programs by name or code..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-72" />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-8">
          {/* Create card — only for super_admin and admin */}
          {canCreateProject() && (
            <div onClick={() => navigate("/create-project")}
              className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-5 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all flex flex-col items-center justify-center text-center min-h-52 gap-3">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Create new project</p>
                <p className="text-xs text-gray-400 mt-1 leading-tight">Start a fresh program baseline with no prior data.</p>
              </div>
              <button onClick={e => { e.stopPropagation(); navigate("/create-project"); }}
                className="px-6 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors">
                Create
              </button>
            </div>
          )}

          {filtered.map((p: any) => {
            const canEnter = !p.viewer_only && (isSuperAdmin() || !isGlobalAdmin());
            const pending = hasPendingRequest(p.id);
            const approved = hasApprovedRequest(p.id);
            return (
              <div key={p.id}
                onClick={() => canEnter && setSelected(p.id)}
                className={`bg-white rounded-xl border-2 p-4 flex flex-col transition-all
                  ${canEnter ? "cursor-pointer" : "cursor-default opacity-80"}
                  ${selected === p.id && canEnter ? "border-blue-600 shadow-md shadow-blue-100" : "border-gray-200 hover:border-gray-300"}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-8 h-8 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold tracking-wide ${statusBadge(p.status)}`}>
                    {p.status?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-1">{p.code} {p.bp_code}</p>
                <p className="text-sm font-bold text-gray-900 mb-3 leading-tight flex-1">{p.name}</p>

                {!p.viewer_only && (
                  <>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {p.last_report_date
                        ? `Last report ${new Date(p.last_report_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                        : "No reports yet"}
                    </div>
                    {p.latest_cpi && (
                      <div className={`flex items-center gap-1.5 text-xs font-medium mb-3 px-2 py-1 rounded-full w-fit
                        ${p.latest_cpi < 0.845 ? "bg-red-50" : p.latest_cpi < 0.945 ? "bg-amber-50" : "bg-green-50"}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${cpiDot(p.latest_cpi)}`}></div>
                        <span className={cpiColor(p.latest_cpi)}>LATEST CPI {p.latest_cpi?.toFixed(2)}</span>
                        {p.latest_vac && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className={p.latest_vac < 0 ? "text-red-500" : "text-green-600"}>
                              {p.latest_vac < 0 ? "-" : "+"}${(Math.abs(p.latest_vac) / 1000000).toFixed(1)}M VAC
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Action button */}
                {p.viewer_only ? (
                  pending ? (
                    <div className="w-full py-1.5 rounded-lg text-sm font-medium text-center bg-orange-50 text-orange-600 border border-orange-200">
                      ⏳ Request Pending
                    </div>
                  ) : approved ? (
                    <div className="w-full py-1.5 rounded-lg text-sm font-medium text-center bg-green-50 text-green-600 border border-green-200">
                      ✓ Access Granted
                    </div>
                  ) : (
                    <button onClick={e => { e.stopPropagation(); openRequestModal(p); }}
                      className="w-full py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors">
                      Request Access
                    </button>
                  )
                ) : canEnter ? (
                  <button onClick={e => { e.stopPropagation(); setSelected(p.id); }}
                    className={`w-full py-1.5 rounded-lg text-sm font-medium transition-colors mt-auto
                      ${selected === p.id ? "bg-blue-50 text-blue-600 border border-blue-200" : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"}`}>
                    {selected === p.id ? "✓ Selected" : "Select"}
                  </button>
                ) : (
                  <div className="w-full py-1.5 rounded-lg text-sm font-medium text-center bg-gray-50 text-gray-400 border border-gray-200">
                    No Access
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!isViewerOnly() && !isGlobalAdmin() && (
          <div className="flex justify-end">
            <button onClick={handleEnter} disabled={!selected}
              className="bg-blue-600 text-white px-8 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              Check Dashboard →
            </button>
          </div>
        )}
      </div>

      {/* Access popup for fresh viewers */}
      {showAccessPopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 text-center mb-2">You don't have project access yet</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              Your account is approved. You can now request access to one or more projects. A project admin will review your request.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowAccessPopup(false)}
                className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Browse Projects
              </button>
              <button onClick={() => setShowAccessPopup(false)}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Access Modal */}
      {showRequestModal && requestForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Request Access</h2>
            <p className="text-sm text-gray-500 mb-4">Request access to <span className="font-medium text-gray-800">{requestForm.project_name}</span></p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Requested Role</label>
              <select value={requestForm.requested_role}
                onChange={e => setRequestForm({ ...requestForm, requested_role: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Reason <span className="text-gray-400">(optional)</span></label>
              <textarea value={requestForm.note}
                onChange={e => setRequestForm({ ...requestForm, note: e.target.value })}
                placeholder="Why do you need access to this project?"
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            {submitMsg && (
              <p className={`text-sm mb-3 ${submitMsg.includes("submitted") ? "text-green-600" : "text-red-500"}`}>{submitMsg}</p>
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowRequestModal(false)}
                className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitRequest}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
